import { format as formatDate, parseISO } from "date-fns";
import { FieldErrors } from "react-hook-form";
import { TApiServiceError } from "../api/apiService";
import { apiURL } from "./env";

export const parseBackendErrors = async (
  error: TApiServiceError,
): Promise<Record<string, string>> => {
  const fieldErrors: Record<string, string> = Object.create(null);

  try {
    // Parse directly from fetch Response (HTTPError has response property)
    if (error.response) {
      const responseBody = (await error.response.json()) as unknown;

      // Type guard to ensure responseBody is an object
      if (
        responseBody &&
        typeof responseBody === "object" &&
        !Array.isArray(responseBody)
      ) {
        for (const [field, errorMessages] of Object.entries(
          responseBody as Record<string, unknown>,
        )) {
          if (
            field === "__proto__" ||
            field === "constructor" ||
            field === "prototype"
          ) {
            continue;
          }
          if (Array.isArray(errorMessages) && errorMessages.length > 0) {
            fieldErrors[field] = String(errorMessages[0]);
          } else if (typeof errorMessages === "string") {
            fieldErrors[field] = errorMessages;
          }
        }
      }
    }

    // Last fallback: parse message if it’s JSON
    if (Object.keys(fieldErrors).length === 0 && error.message) {
      try {
        const messageData = JSON.parse(error.message) as unknown;

        // Type guard to ensure messageData is an object
        if (
          messageData &&
          typeof messageData === "object" &&
          !Array.isArray(messageData)
        ) {
          for (const [field, errorMessages] of Object.entries(
            messageData as Record<string, unknown>,
          )) {
            if (
              field === "__proto__" ||
              field === "constructor" ||
              field === "prototype"
            ) {
              continue;
            }
            if (Array.isArray(errorMessages) && errorMessages.length > 0) {
              fieldErrors[field] = String(errorMessages[0]);
            } else if (typeof errorMessages === "string") {
              fieldErrors[field] = errorMessages;
            }
          }
        }
      } catch {
        // message wasn’t JSON
      }
    }
  } catch (parseError) {
    console.error("Error parsing backend validation errors:", parseError);
  }

  return fieldErrors;
};

export function merge<T extends object, U extends object[]>(
  target: T,
  ...sources: U
): T & U[number] {
  const isObject = (item: unknown): item is object =>
    item !== null && typeof item === "object" && !Array.isArray(item);

  for (const source of sources) {
    for (const key of Object.keys(source)) {
      const targetValue = target[key as keyof T];
      const sourceValue = source[key as keyof typeof source];

      if (isObject(targetValue) && isObject(sourceValue)) {
        // Recursive merge for nested objects
        target[key as keyof T] = merge(targetValue, sourceValue);
      } else if (sourceValue !== undefined) {
        // Overwrite primitives and undefined values from target
        target[key as keyof T] = sourceValue;
      }
    }
  }

  return target;
}

const generateUUID = function () {
  let counter = 0;
  return function ({ simple = false } = {}) {
    counter += 1;
    if (simple) return counter.toString();

    return (
      "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxx".replace(/[xy]/g, function (c) {
        const r = (Math.random() * 16) | 0,
          v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      }) + `-${counter.toString()}`
    );
  };
};

export const uuid = generateUUID();

export function generateUniqueRandomNumbers(
  length: number,
  min: number,
  max: number,
) {
  if (length > max - min + 1) {
    throw new Error(
      "Cannot generate unique random numbers. Requested length is greater than the range of numbers.",
    );
  }

  const numbers = new Set();
  while (numbers.size < length) {
    numbers.add(Math.floor(Math.random() * (max - min + 1)) + min);
  }
  return Array.from(numbers) as number[];
}

type TTruncatePosition = "start" | "middle" | "end";

export function truncateText(
  text: string,
  options: {
    maxLength: number;
    position?: TTruncatePosition;
    ellipsis?: string;
  },
): string {
  const { maxLength, position = "end", ellipsis = "..." } = options;

  if (text.length <= maxLength) {
    return text;
  }

  const ellipsisLength = ellipsis.length;
  const leftLength = Math.ceil((maxLength - ellipsisLength) / 2);
  const rightLength = Math.floor((maxLength - ellipsisLength) / 2);

  switch (position) {
    case "start":
      return ellipsis + text.slice(-(maxLength - ellipsisLength));

    case "middle":
      return text.slice(0, leftLength) + ellipsis + text.slice(-rightLength);

    case "end":
    default:
      return text.slice(0, maxLength - ellipsisLength) + ellipsis;
  }
}
export const convertDateISO = (date: string, format: string) => {
  const cleanedDate = date.split(".")[0] + "Z";
  return formatDate(parseISO(cleanedDate), format);
};

// Helper to convert hex to HSL
export function hexToHSL(hex: string): string {
  // Remove '#' if present
  hex = hex.replace(/^#/, "");
  // Parse r, g, b
  let r = 0,
    g = 0,
    b = 0;
  if (hex.length === 3) {
    r = parseInt(hex[0] + hex[0], 16);
    g = parseInt(hex[1] + hex[1], 16);
    b = parseInt(hex[2] + hex[2], 16);
  } else if (hex.length === 6) {
    r = parseInt(hex.substring(0, 2), 16);
    g = parseInt(hex.substring(2, 4), 16);
    b = parseInt(hex.substring(4, 6), 16);
  }
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b),
    min = Math.min(r, g, b);
  let h = 0,
    s = 0,
    l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }
  h = Math.round(h * 360);
  s = Math.round(s * 100);
  l = Math.round(l * 100);
  return `${h} ${s}% ${l}%`;
}

// Helper to convert HSL string to RGB
export function hslToRgb(
  h: number,
  s: number,
  l: number,
): [number, number, number] {
  s /= 100;
  l /= 100;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0,
    g = 0,
    b = 0;
  if (0 <= h && h < 60) {
    r = c;
    g = x;
    b = 0;
  } else if (60 <= h && h < 120) {
    r = x;
    g = c;
    b = 0;
  } else if (120 <= h && h < 180) {
    r = 0;
    g = c;
    b = x;
  } else if (180 <= h && h < 240) {
    r = 0;
    g = x;
    b = c;
  } else if (240 <= h && h < 300) {
    r = x;
    g = 0;
    b = c;
  } else if (300 <= h && h < 360) {
    r = c;
    g = 0;
    b = x;
  }
  r = Math.round((r + m) * 255);
  g = Math.round((g + m) * 255);
  b = Math.round((b + m) * 255);
  return [r, g, b];
}

// Helper to get contrast color (black or white) for given HSL
export function getContrastForHSL(hsl: string): string {
  const [h, s, l] = hsl.replace(/%/g, "").split(" ").map(Number);
  const [r, g, b] = hslToRgb(h, s, l);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 128 ? "#000" : "#fff";
}

export function getContrastFromHex(hex: string): string {
  if (hex) {
    // Remove '#' if present
    hex = hex?.replace(/^#/, "");
    // Parse r, g, b
    let r = 0,
      g = 0,
      b = 0;
    if (hex.length === 3) {
      r = parseInt(hex[0] + hex[0], 16);
      g = parseInt(hex[1] + hex[1], 16);
      b = parseInt(hex[2] + hex[2], 16);
    } else if (hex.length === 6) {
      r = parseInt(hex.substring(0, 2), 16);
      g = parseInt(hex.substring(2, 4), 16);
      b = parseInt(hex.substring(4, 6), 16);
    }
    const yiq = (r * 299 + g * 587 + b * 114) / 1000;
    return yiq >= 128 ? "#000" : "#fff";
  }
  return "";
}

export function hslStringToHex(hsl: string): string {
  if (hsl?.startsWith("hsl")) {
    const hslRegex =
      /hsl\(\s*(\d+(?:\.\d+)?)\s*,?\s*(\d+(?:\.\d+)?)%\s*,?\s*(\d+(?:\.\d+)?)%\s*\)/i;
    const match = hslRegex?.exec(hsl);

    if (!match) {
      return "";
    }

    const [, h, s, l] = match.map(Number);
    return hslToHex(h, s, l);
  }
  return "";
}

// Reuse the existing hslToHex function
function hslToHex(h: number, s: number, l: number): string {
  s /= 100;
  l /= 100;

  const k = (n: number) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) =>
    Math.round(
      255 * (l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)))),
    );

  return `#${f(0).toString(16).padStart(2, "0")}${f(8)
    .toString(16)
    .padStart(2, "0")}${f(4).toString(16).padStart(2, "0")}`;
}

// call this function to show array msg in string way from frontend
export function flattenErrorsToString(errors: FieldErrors): string {
  const messages: string[] = [];

  function recurse(obj: FieldErrors, path = "") {
    for (const key in obj) {
      const value = obj[key];
      if (!value) continue;

      if ("message" in value && typeof value.message === "string") {
        messages.push(`${path}${key}: ${value.message}`);
      } else if (typeof value === "object") {
        recurse(value as FieldErrors, `${path}${key}.`);
      }
    }
  }

  recurse(errors);
  return messages.join(" | ");
}

/**
 * Simple slugify function to convert a string to a URL-friendly slug.
 * @param str - The string to slugify.
 * @returns The slugified string.
 */
export function simpleSlugify(str: string): string {
  return str
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "") // remove special chars
    .replace(/\s+/g, "-") // replace spaces with hyphens
    .replace(/-+/g, "-"); // collapse multiple hyphens
}

export function getRootStyles(key: string) {
  const styles = getComputedStyle(document.documentElement);
  return hslStringToHex(`hsl(${styles.getPropertyValue(key).trim()})`);
}

export function applyRootStyles(
  key: string,
  theme: string,
  lightThemeValue: string,
  darkThemeValue: string,
) {
  const root = window.document.documentElement;
  return root.style.setProperty(
    key,
    theme === "dark" ? hexToHSL(darkThemeValue) : hexToHSL(lightThemeValue),
  );
}
export const convertToFile = async (val: File | string): Promise<File> => {
  if (val instanceof File) return val;

  const src = String(val).trim();
  const normalizedSrc = src.replace(/^\/+/g, "/");

  // ✅ Normalize API URLs so fetch doesn’t hit Vite
  const url = normalizedSrc.startsWith("http")
    ? normalizedSrc
    : apiURL(normalizedSrc).toString();

  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) throw new Error(`Failed to fetch ${url}: ${res.status}`);
  const blob = await res.blob();

  if (!blob.type.startsWith("image/")) {
    throw new Error(`URL ${url} did not return an image, got ${blob.type}`);
  }

  const name = url.split("/").pop() ?? getFileName(url);
  return new File([blob], name, { type: blob.type });
};

export const getFileName = (url: string) => {
  if (!url) return "";
  return url.split("/").pop() ?? "";
};

export function setFavicon(url: string | File) {
  // Remove all existing favicon links
  const existingLinks = document.querySelectorAll("link[rel*='icon']");
  existingLinks.forEach((link) => link.remove());

  // Create new favicon link
  const link = document.createElement("link");
  link.rel = "icon";
  link.type = "image/svg+xml"; // Specify MIME type

  const finalUrl =
    typeof url === "string" ? `${url}` : URL.createObjectURL(url);

  link.href = finalUrl;
  document.head.appendChild(link);
}

export const getAgentName = (array: any[], id: string) => {
  const agent = array?.find((a) => a.internal_id === id);
  if (agent) {
    return `${agent.first_name} ${agent.last_name}`.trim();
  }
  return "";
};

export const getSupervisorName = (array: any[], id: string) => {
  const supervisor = array?.find((a) => a.internal_id === id);
  if (supervisor) {
    return `${supervisor.first_name} ${supervisor.last_name}`.trim();
  }
  return "";
};

export const getCampaignName = (array: any[], id: string) => {
  const campaign = array?.find((a) => a.internal_id === id);
  if (campaign) {
    return `${campaign.name}`?.trim();
  }
  return "";
};

export const getServiceName = (array: any[], id: string) => {
  const service = array?.find((a) => a.internal_id === id);
  if (service) {
    return `${service.name}`?.trim();
  }
  return "";
};

export const formatDuration = (seconds: number) => {
  if (typeof seconds !== "number" || seconds < 0) return "0 sec";

  if (seconds < 60) {
    return `${seconds} sec`;
  } else if (seconds < 3600) {
    return `${Math.round(seconds / 60)} m`;
  } else {
    return `${(seconds / 3600).toFixed(1)} hr`; // 1 decimal for hours
  }
};

const FREQUENCY_LABEL_MAP: Record<string, string> = {
  daily: "Daily",
  weekly: "Weekly",
  monthly: "Monthly",
};

// Used by filter chips to show a readable frequency label.
export const getFrequencyFilterLabel = (value: string): string =>
  FREQUENCY_LABEL_MAP[value] ?? value;

// Used by filter chips to show a readable AI compliance label.
export const getAiComplianceFilterLabel = (value: string): string =>
  value === "True" || value === "Yes" ? "Yes" : "No";
